import { ErrorReporter } from "@ai-teach/utils";
import { ApolloProvider } from "@apollo/client/react";
import { RouterProvider, createRouter } from "@tanstack/react-router";
import React from "react";
import ReactDOM from "react-dom/client";
import { Toaster } from "react-hot-toast";
import { Provider as ReduxProvider } from "react-redux";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { SlideScreenshotQueueProvider } from "./components/slides/screenshot/SlideScreenshotQueue";
import { AuthProvider } from "./contexts/AuthContext";
import { ThemeProvider } from "./contexts/ThemeContext";
import { LocaleProvider } from "./i18n";
import { apolloClient } from "./lib/graphql-client";
import { store } from "./store/store";
import "./styles/theme.css";
import "./styles/globals.css";

// Import the generated route tree
import { routeTree } from "./routeTree.gen";

// Initialize ErrorReporter for web
ErrorReporter.init({
  platform: "web",
  appVersion: "1.0.0",
  apiUrl: import.meta.env.VITE_API_BASE_URL || "http://localhost:4000",
  environment: import.meta.env.MODE === "production" ? "prod" : "dev",
});

// Global error handler for unhandled errors
window.onerror = (message, source, lineno, colno, error) => {
  ErrorReporter.log(error || new Error(String(message)), {
    screen: window.location.pathname,
    metadata: { source, lineno, colno },
  });
  return false; // Let default error handling continue
};

// Global handler for unhandled promise rejections
window.addEventListener("unhandledrejection", (event) => {
  ErrorReporter.log(
    event.reason instanceof Error ? event.reason : new Error(String(event.reason)),
    {
      screen: window.location.pathname,
      action: "unhandledRejection",
    }
  );
});

// Create a new router instance
const router = createRouter({ routeTree, scrollRestoration: true });

// Register the router instance for type safety
declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

const root = document.getElementById("root");

if (!root) {
  throw new Error("Root element not found");
}

ReactDOM.createRoot(root).render(
  <React.StrictMode>
    <ErrorBoundary>
      <ThemeProvider>
        <LocaleProvider>
          <ReduxProvider store={store}>
            <ApolloProvider client={apolloClient}>
              <AuthProvider>
                <SlideScreenshotQueueProvider>
                  <Toaster containerStyle={{ zIndex: 100000 }} />
                  <RouterProvider router={router} />
                </SlideScreenshotQueueProvider>
              </AuthProvider>
            </ApolloProvider>
          </ReduxProvider>
        </LocaleProvider>
      </ThemeProvider>
    </ErrorBoundary>
  </React.StrictMode>
);
